import Phaser from "phaser";
import { Prop, Tile } from "../tiles";

/**
 * "Mid-floor" tile: a solid tile that anchors the player at the tile's VERTICAL MIDDLE
 * (feet on the mid-line) instead of at its top, so a floor can be nudged half a tile down
 * to line up exactly with the backdrop art. It is the LAST office tile (id 7); it also
 * responds to a `midfloor` custom property if the tileset carries one.
 *
 * Arcade tile collision only ever uses the FULL tile rect, so a half-height surface can't
 * come from the tile itself. Instead we give each mid-floor tile a static body covering
 * its bottom half (top edge at the tile's middle); the tile's own collision stays off.
 * This mirrors Level2LucasScene.buildLiftedFloors, which uses the same lifted-body trick.
 */

/** True when `tile` is a mid-floor tile: carries the `midfloor` property, or is the last
 *  office tile (id 7). The id check keeps it working even where the embedded tileset does
 *  not carry the property yet (our maps embed the tileset, so props lag the source). */
export function isMidFloorTile(tile: Phaser.Tilemaps.Tile, firstgid: number): boolean {
  if (tile.properties && (tile.properties as Record<string, unknown>)[Prop.MIDFLOOR]) return true;
  return tile.index - firstgid === Tile.MIDFLOOR;
}

/** World y of a mid-floor tile's anchor surface: its vertical middle. */
export function midFloorSurfaceY(tile: Phaser.Tilemaps.Tile): number {
  return tile.pixelY + tile.height / 2;
}

/** Build solid static bodies for every mid-floor tile in `layer`, each covering the tile's
 *  bottom half so the player rests on the tile's middle. Returns the static group (empty if
 *  there are none). Add a plain collider between the player and this group. */
export function buildMidFloorBodies(
  scene: Phaser.Scene,
  layer: Phaser.Tilemaps.TilemapLayer,
  firstgid: number,
): Phaser.Physics.Arcade.StaticGroup {
  const group = scene.physics.add.staticGroup();
  const tw = layer.tilemap.tileWidth;
  const th = layer.tilemap.tileHeight;
  layer.forEachTile((t) => {
    if (!isMidFloorTile(t, firstgid)) return;
    // Bottom half of the tile: top edge at the tile's vertical middle (pixelY + th/2),
    // so the player's feet come to rest there.
    const zone = scene.add.zone(t.pixelX + tw / 2, t.pixelY + th * 0.75, tw, th / 2);
    scene.physics.add.existing(zone, true);
    group.add(zone);
  });
  return group;
}
